import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OidcAuthService } from '../oidc-auth.service';
import { LoginService } from '../../login/login.service';

/**
 * Lands the Authorization Code redirect from QLM: exchanges the code (via PKCE) for
 * tokens, then routes onward. Dedicated route (/auth/callback) so the "start login" and
 * "finish login" concerns never tangle on the login page.
 */
@Component({
  selector: 'oidc-callback',
  templateUrl: './oidc-callback.component.html',
  styleUrls: ['./oidc-callback.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OidcCallbackComponent implements OnInit {
  constructor(
    private readonly oidc: OidcAuthService,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
    private readonly loginService: LoginService,
  ) {}

  async ngOnInit(): Promise<void> {
    // A silent (prompt=none) attempt with no AS session comes back as
    // ?error=login_required (not a code). Fall back to the login page with silent=failed
    // so it shows the button instead of re-attempting silently (which would loop).
    const error = new URLSearchParams(window.location.search).get('error');
    if (error) {
      this.router.navigate(['/login'], { queryParams: { silent: 'failed' } });
      return;
    }
    try {
      const ok = await this.oidc.completeLogin();
      if (!ok) {
        this.router.navigate(['/login']);
        return;
      }
      // Load user info before navigating: the route guards require hasInfo, and on the
      // OIDC path LoginService serves it from memory once fetched here.
      await this.loginService.getUserInfo();
      const target = this.oidc.postLoginTarget;
      this.router.navigateByUrl(
        target && target.startsWith('/') ? target : '/portal/pseudocode',
      );
    } catch {
      this.snackBar.open('Login failed. Please try again.', 'dismiss', {
        duration: 3000,
        verticalPosition: 'top',
        horizontalPosition: 'right',
      });
      this.router.navigate(['/login']);
    }
  }
}
